import { GuardianTypeEnum } from 'apps/user/types/user.enum.type';
import { useCreateGuardian } from 'common/queries-and-mutations/guardian';
import Button from 'components/Form/Button';
import CustomSelect from 'components/Form/CustomSelect';
import TextField from 'components/Form/TextField';
import { Icon, Icons } from 'components/Icon';
import { InfoModal } from 'components/Modal';
import Spacer from 'components/Spacer';
import { Alert } from 'components/Toast';
import { RelationshipType } from 'constants/bank-account-type';
import { MaritalStatus } from 'constants/marital-status';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { services } from 'service';
import * as Yup from 'yup';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
  disableType?: boolean;
};

export default function AddGuardianModal({
  onClose,
  onSuccess,
  disableType = true
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate, isSuccess, isLoading, isError } = useCreateGuardian();

  const formik = useFormik({
    initialValues: {
      guardians: [
        {
          guardianId: null,
          guardianFirstName: '',
          guardianMiddleName: '',
          guardianLastName: '',
          guardianType: GuardianTypeEnum.PRIMARY,
          relationship: '',
          maritalStatus: '',
          address: '',
          emailAddress: '',
          phoneNumber: '',
          dateOfBirth: ''
        }
      ]
    },
    validationSchema: Yup.object({
      guardians: Yup.array().of(
        Yup.object().shape({
          guardianFirstName: Yup.string()
            .required('Guardian first name is required')
            .nullable(),
          // guardianMiddleName: Yup.string()
          //   .required('Guardian middle name is required')
          //   .nullable(),
          guardianLastName: Yup.string()
            .required('Guardian last name is required')
            .nullable(),
          guardianType: Yup.string()
            .required('Guardian type is required')
            .nullable(),
          relationship: Yup.string()
            .required('Relationship is required')
            .nullable(),
          maritalStatus: Yup.string()
            .required('Marital status is required')
            .nullable(),
          address: Yup.string().required('Home address is required').nullable(),
          emailAddress: Yup.string()
            .email('Invalid email')
            .required('Email is required')
            .nullable()
            .test('is-allowed', 'Email already in use', async function (value) {
              if (value) {
                const isInUse =
                  await services.guardianService.checkIfEmailInUse(value);
                return !isInUse;
              }
              return true;
            }),
          phoneNumber: Yup.string()
            .required('Phone number is required')
            .nullable(),
          dateOfBirth: Yup.date()
            .required('Date of Birth is required')
            .nullable()
        })
      )
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onGuardianSubmit(values);
    }
  });

  const onGuardianSubmit = (formValue: any) => {
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      toast(<Alert message="Guardian Successfully Added" type="success" />);
      onSuccess();
      setIsSubmitting(false);
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError]);

  return (
    <InfoModal
      width="w-[55%]"
      className="h-fit rounded-md px-4 pb-9 pt-5 sm:p-9"
    >
      <div>
        <div className="flex flex-row items-center justify-between pl-6">
          <p className="text-2xl font-bold text-[#353535]">Add Guardian</p>
          <button type="button" onClick={onClose}>
            <Icon name={Icons.Close} />
          </button>
        </div>
        <form
          autoComplete="false"
          className="mt-10 px-6"
          onSubmit={formik.handleSubmit}
        >
          {formik.values.guardians.map((guardian, index) => (
            <div key={index} className="mb-1 mt-8">
              <div className="grid md:grid-cols-2 md:gap-10">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`guardians[${index}].guardianFirstName`}
                    placeholder=""
                    value={guardian.guardianFirstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Guardian First Name"
                    error={
                      formik.touched.guardians
                        ? formik.errors.guardians?.[index] &&
                          formik.errors.guardians.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.guardians[index])
                            ).guardianFirstName
                          : ''
                        : ''
                    }
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`guardians[${index}].guardianMiddleName`}
                    placeholder=""
                    value={guardian.guardianMiddleName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Guardian Middle Name"
                    error={
                      formik.touched.guardians
                        ? formik.errors.guardians?.[index] &&
                          formik.errors.guardians.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.guardians[index])
                            ).guardianMiddleName
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 md:gap-10">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`guardians[${index}].guardianLastName`}
                    placeholder=""
                    value={guardian.guardianLastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Guardian Last Name"
                    error={
                      formik.touched.guardians
                        ? formik.errors.guardians?.[index] &&
                          formik.errors.guardians.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.guardians[index])
                            ).guardianLastName
                          : ''
                        : ''
                    }
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <div className="relative mb-2 flex items-center justify-between">
                    <span className="flex items-center">
                      <label className="text-[12px] font-medium text-[#353535]">
                        Type of Guardian
                      </label>
                      <div className="mx-2 mb-2">
                        <Spacer width={3} />
                        <Icon name={Icons.Required} />
                      </div>
                    </span>
                  </div>
                  <div className="mt-5 flex items-center gap-10">
                    <button
                      type="button"
                      disabled={disableType}
                      onClick={() =>
                        formik.setFieldValue(
                          `guardians[${index}].guardianType`,
                          GuardianTypeEnum.PRIMARY
                        )
                      }
                      className="flex items-center gap-3"
                    >
                      <span className="text-base font-bold text-[#353535]">
                        Primary
                      </span>
                      <Icon
                        name={
                          guardian.guardianType === GuardianTypeEnum.PRIMARY
                            ? Icons.CheckedBox
                            : Icons.UncheckBox
                        }
                      />
                    </button>
                    <button
                      type="button"
                      disabled={disableType}
                      onClick={() =>
                        formik.setFieldValue(
                          `guardians[${index}].guardianType`,
                          GuardianTypeEnum.SUBSTITUTE
                        )
                      }
                      className="flex items-center gap-3"
                    >
                      <span className="text-base font-bold text-[#353535]">
                        Substitute
                      </span>
                      <Icon
                        name={
                          guardian.guardianType === GuardianTypeEnum.SUBSTITUTE
                            ? Icons.CheckedBox
                            : Icons.UncheckBox
                        }
                      />
                    </button>
                  </div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 md:gap-10">
                <div className="group relative z-0 mb-6 w-full">
                  <CustomSelect
                    options={RelationshipType}
                    selectedOption={{
                      value: guardian.relationship,
                      label: guardian.relationship
                    }}
                    handleOptionChange={option => {
                      formik.setFieldValue(
                        `guardians[${index}].relationship`,
                        option?.value
                      );
                    }}
                    label="Relationship"
                    name="relationship"
                    isRequired
                    isSearchable
                    className="h-[3.5rem] rounded-2xl"
                    error={
                      formik.touched.guardians
                        ? formik.errors.guardians?.[index] &&
                          formik.errors.guardians.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.guardians[index])
                            ).relationship
                          : ''
                        : ''
                    }
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <CustomSelect
                    options={MaritalStatus}
                    selectedOption={{
                      value: guardian.maritalStatus,
                      label: guardian.maritalStatus
                    }}
                    handleOptionChange={option => {
                      formik.setFieldValue(
                        `guardians[${index}].maritalStatus`,
                        option?.value
                      );
                    }}
                    label="Marital Status"
                    name="maritalStatus"
                    isRequired
                    isSearchable
                    className="h-[3.5rem] rounded-2xl"
                    error={
                      formik.touched.guardians
                        ? formik.errors.guardians?.[index] &&
                          formik.errors.guardians.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.guardians[index])
                            ).maritalStatus
                          : ''
                        : ''
                    }
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 md:gap-10">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`guardians[${index}].address`}
                    placeholder=""
                    isRequired
                    value={guardian.address}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Home Address"
                    error={
                      formik.touched.guardians
                        ? formik.errors.guardians?.[index] &&
                          formik.errors.guardians.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.guardians[index])
                            ).address
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`guardians[${index}].emailAddress`}
                    placeholder=""
                    isRequired
                    value={guardian.emailAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Email Address"
                    error={
                      formik.touched.guardians
                        ? formik.errors.guardians?.[index] &&
                          formik.errors.guardians.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.guardians[index])
                            ).emailAddress
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 md:gap-10">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`guardians[${index}].phoneNumber`}
                    placeholder=""
                    isRequired
                    value={guardian.phoneNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Phone Number"
                    error={
                      formik.touched.guardians
                        ? formik.errors.guardians?.[index] &&
                          formik.errors.guardians.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.guardians[index])
                            ).phoneNumber
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`guardians[${index}].dateOfBirth`}
                    type="date"
                    isRequired
                    placeholder=""
                    value={guardian.dateOfBirth}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Date of Birth"
                    error={
                      formik.touched.guardians
                        ? formik.errors.guardians?.[index] &&
                          formik.errors.guardians.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.guardians[index])
                            ).dateOfBirth
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between gap-2">
            <div className="flex w-full items-center gap-2">
              <Icon name={Icons.Required} />
              <span className="text-[10px] text-[#7A7C87]">
                Indicates Required
              </span>
            </div>
          </div>

          <div className="mt-5 flex items-end justify-end gap-8">
            <Button
              variant="primary"
              size="md"
              className="mt-5 w-[9rem] rounded p-[0.1rem]"
              label="Proceed"
              type="submit"
              disabled={!formik.isValid}
              loading={isSubmitting || isLoading}
            ></Button>
          </div>
        </form>
      </div>
    </InfoModal>
  );
}
